
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/auth';
import { jwtDecode } from 'jwt-decode';

// Debug helper - only logs in development
const debugSession = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[CentralizedSession] ${message}`, data || '');
  }
};

// Generate unique session token
const generateSessionToken = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2)}`;
};

// Cross-tab communication events
export type SessionEvent = 
  | { type: 'SESSION_UPDATED'; payload: { user: UserProfile | null; sessionToken: string } }
  | { type: 'SESSION_EXPIRED'; payload: { reason: string } }
  | { type: 'SESSION_CONFLICT'; payload: { conflictType: string } }
  | { type: 'LOGOUT'; payload: {} };

export interface SessionState {
  user: UserProfile | null;
  sessionToken: string | null;
  isLoading: boolean;
  lastActivity: number;
  error: string | null;
}

export interface SessionStorage {
  user?: UserProfile;
  sessionToken?: string;
  lastActivity?: number;
  expiresAt?: number;
}

class CentralizedSessionService {
  private currentState: SessionState = {
    user: null,
    sessionToken: null,
    isLoading: true,
    lastActivity: Date.now(),
    error: null
  };

  private listeners: Set<(state: SessionState) => void> = new Set();
  private broadcastChannel: BroadcastChannel | null = null;
  private activityTimer: number | null = null;
  private sessionCheckTimer: number | null = null;
  private storageKey = 'secure_session_data';
  private encryptionKey = 'session_encryption_key_v1';

  constructor() {
    this.initializeBroadcastChannel();
    this.initializeActivityMonitoring();
    this.initializeSessionCheck();
  }

  // Initialize cross-tab communication
  private initializeBroadcastChannel() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.broadcastChannel = new BroadcastChannel('session_sync');
      this.broadcastChannel.onmessage = (event: MessageEvent<SessionEvent>) => {
        this.handleCrossTabMessage(event.data);
      };
      debugSession('BroadcastChannel initialized');
    }
  }

  // Handle messages from other tabs
  private handleCrossTabMessage(event: SessionEvent) {
    debugSession('Received cross-tab message', event);
    
    switch (event.type) {
      case 'SESSION_UPDATED':
        // Priority: if another tab has newer session, adopt it
        const currentActivity = this.getStoredLastActivity();
        const otherActivity = this.currentState.lastActivity;
        
        if (otherActivity > currentActivity) {
          this.updateState({
            user: event.payload.user,
            sessionToken: event.payload.sessionToken,
            lastActivity: otherActivity
          });
        }
        break;
        
      case 'SESSION_EXPIRED':
        this.handleSessionExpired(event.payload.reason);
        break;
        
      case 'SESSION_CONFLICT':
        this.handleSessionConflict(event.payload.conflictType);
        break;
        
      case 'LOGOUT':
        this.clearSession();
        break;
    }
  }

  // Broadcast session event to other tabs
  private broadcastEvent(event: SessionEvent) {
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(event);
      debugSession('Broadcasted event', event);
    }
  }

  // Secure storage methods with encryption fallback
  private encryptData(data: string): string {
    try {
      // Simple XOR encryption for basic protection
      return btoa(data.split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length))
      ).join(''));
    } catch {
      return data; // Fallback to unencrypted
    }
  }

  private decryptData(encryptedData: string): string {
    try {
      const decoded = atob(encryptedData);
      return decoded.split('').map((char, i) => 
        String.fromCharCode(char.charCodeAt(0) ^ this.encryptionKey.charCodeAt(i % this.encryptionKey.length))
      ).join('');
    } catch {
      return encryptedData; // Assume it's unencrypted
    }
  }

  private secureStorage = {
    setItem: (key: string, value: string): boolean => {
      try {
        const encrypted = this.encryptData(value);
        localStorage.setItem(key, encrypted);
        return true;
      } catch (error) {
        debugSession('Error storing in localStorage, trying sessionStorage', error);
        try {
          const encrypted = this.encryptData(value);
          sessionStorage.setItem(key, encrypted);
          return true;
        } catch (sessionError) {
          debugSession('Error storing in sessionStorage', sessionError);
          return false;
        }
      }
    },

    getItem: (key: string): string | null => {
      try {
        const item = localStorage.getItem(key);
        return item ? this.decryptData(item) : null;
      } catch (error) {
        debugSession('Error reading from localStorage, trying sessionStorage', error);
        try {
          const item = sessionStorage.getItem(key);
          return item ? this.decryptData(item) : null;
        } catch (sessionError) {
          debugSession('Error reading from sessionStorage', sessionError);
          return null;
        }
      }
    },

    removeItem: (key: string): void => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        debugSession('Error removing from localStorage', error);
      }
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        debugSession('Error removing from sessionStorage', error);
      }
    }
  };

  // Initialize activity monitoring
  private initializeActivityMonitoring() {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const updateActivity = () => {
      this.updateLastActivity();
    };

    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Set up activity check timer
    this.activityTimer = window.setInterval(() => {
      this.checkSessionActivity();
    }, 60000); // Check every minute
  }

  // Initialize periodic session validation
  private initializeSessionCheck() {
    this.sessionCheckTimer = window.setInterval(() => {
      this.validateCurrentSession();
    }, 30000); // Check every 30 seconds
  }

  // Update last activity timestamp
  private updateLastActivity() {
    const now = Date.now();
    this.currentState.lastActivity = now;
    this.saveSessionToStorage();
  }

  // Check if session has timed out
  private checkSessionActivity() {
    const now = Date.now();
    const timeSinceActivity = now - this.currentState.lastActivity;
    const timeoutDuration = 60 * 60 * 1000; // 60 minutes

    if (timeSinceActivity > timeoutDuration) {
      debugSession('Session timed out due to inactivity');
      this.handleSessionExpired('inactivity_timeout');
    }
  }

  // Validate current session with Supabase
  private async validateCurrentSession(): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        debugSession('No valid Supabase session found');
        return false;
      }

      // Check JWT expiration
      if (this.isTokenExpired(session.access_token)) {
        debugSession('JWT token expired, attempting refresh');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          debugSession('Token refresh failed');
          this.handleSessionExpired('token_refresh_failed');
          return false;
        }
      }

      return true;
    } catch (error) {
      debugSession('Error validating session', error);
      return false;
    }
  }

  // Check if JWT token is expired
  private isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime + 10; // 10 second buffer
    } catch {
      return true;
    }
  }

  // Handle session expiration
  private handleSessionExpired(reason: string) {
    debugSession('Handling session expiration', { reason });
    this.clearSession();
    this.broadcastEvent({ type: 'SESSION_EXPIRED', payload: { reason } });
    this.updateState({ error: `Session expired: ${reason}` });
  }

  // Handle session conflicts
  private handleSessionConflict(conflictType: string) {
    debugSession('Handling session conflict', { conflictType });
    // For now, clear the session and let user re-authenticate
    this.clearSession();
    this.updateState({ error: `Session conflict: ${conflictType}` });
  }

  // Save session data to storage
  private saveSessionToStorage() {
    if (this.currentState.user && this.currentState.sessionToken) {
      const sessionData: SessionStorage = {
        user: this.currentState.user,
        sessionToken: this.currentState.sessionToken,
        lastActivity: this.currentState.lastActivity,
        expiresAt: Date.now() + (60 * 60 * 1000) // 1 hour from now
      };

      this.secureStorage.setItem(this.storageKey, JSON.stringify(sessionData));
    }
  }

  // Load session data from storage
  private loadSessionFromStorage(): SessionStorage | null {
    try {
      const stored = this.secureStorage.getItem(this.storageKey);
      if (!stored) return null;

      const sessionData: SessionStorage = JSON.parse(stored);
      
      // Check if stored session is expired
      if (sessionData.expiresAt && sessionData.expiresAt < Date.now()) {
        debugSession('Stored session expired');
        this.secureStorage.removeItem(this.storageKey);
        return null;
      }

      return sessionData;
    } catch (error) {
      debugSession('Error loading session from storage', error);
      return null;
    }
  }

  // Get stored last activity timestamp
  private getStoredLastActivity(): number {
    const stored = this.loadSessionFromStorage();
    return stored?.lastActivity || 0;
  }

  // Update state and notify listeners
  private updateState(updates: Partial<SessionState>) {
    this.currentState = { ...this.currentState, ...updates };
    this.saveSessionToStorage();
    this.notifyListeners();
  }

  // Notify all listeners of state change
  private notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentState);
      } catch (error) {
        debugSession('Error in listener callback', error);
      }
    });
  }

  // Clear all session data
  private clearSession() {
    this.secureStorage.removeItem(this.storageKey);
    this.updateState({
      user: null,
      sessionToken: null,
      error: null
    });
  }

  // Public API methods

  // Subscribe to session state changes
  subscribe(listener: (state: SessionState) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.currentState);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current session state
  getState(): SessionState {
    return { ...this.currentState };
  }

  // Initialize session from storage or Supabase
  async initializeSession(): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      // First try to load from storage
      const storedSession = this.loadSessionFromStorage();
      if (storedSession?.user && storedSession.sessionToken) {
        debugSession('Found stored session', { userId: storedSession.user.id });
        
        // Validate with Supabase
        const isValid = await this.validateCurrentSession();
        if (isValid) {
          this.updateState({
            user: storedSession.user,
            sessionToken: storedSession.sessionToken,
            lastActivity: storedSession.lastActivity || Date.now(),
            isLoading: false
          });
          return;
        } else {
          debugSession('Stored session invalid, clearing');
          this.clearSession();
        }
      }

      // No valid stored session, check Supabase
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) throw error;

      if (session?.user) {
        debugSession('Found Supabase session', { userId: session.user.id });
        await this.createSessionFromSupabase(session);
      } else {
        debugSession('No session found');
        this.updateState({ isLoading: false });
      }
    } catch (error) {
      debugSession('Error initializing session', error);
      this.updateState({ 
        isLoading: false, 
        error: 'Failed to initialize session' 
      });
    }
  }

  // Create session from Supabase session
  private async createSessionFromSupabase(session: any): Promise<void> {
    try {
      // Create user profile
      const userProfile: UserProfile = {
        id: session.user.id,
        email: session.user.email || '',
        name: session.user.user_metadata?.name || '',
        phone: session.user.user_metadata?.phone || '',
        age: session.user.user_metadata?.age || 21,
        educationStatus: session.user.user_metadata?.education_status || 'completed12th'
      };

      const sessionToken = generateSessionToken();

      // Store session in database
      await this.storeSessionInDatabase(sessionToken, session.user.id);

      this.updateState({
        user: userProfile,
        sessionToken,
        isLoading: false,
        lastActivity: Date.now()
      });

      this.broadcastEvent({
        type: 'SESSION_UPDATED',
        payload: { user: userProfile, sessionToken }
      });

      debugSession('Session created successfully', { userId: userProfile.id });
    } catch (error) {
      debugSession('Error creating session from Supabase', error);
      throw error;
    }
  }

  // Store session in database
  private async storeSessionInDatabase(sessionToken: string, userId: string): Promise<void> {
    try {
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      };

      const { error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_token: sessionToken,
          device_info: deviceInfo,
          expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString() // 1 hour
        });

      if (error) throw error;
      debugSession('Session stored in database');
    } catch (error) {
      debugSession('Error storing session in database', error);
      // Don't throw here - continue with local session even if DB storage fails
    }
  }

  // Login method
  async login(email: string, password: string): Promise<void> {
    this.updateState({ isLoading: true, error: null });

    try {
      // Clear any existing session
      await this.logout();

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      if (!data.session) throw new Error('No session returned');

      await this.createSessionFromSupabase(data.session);
    } catch (error: any) {
      debugSession('Login error', error);
      this.updateState({ 
        isLoading: false, 
        error: error.message || 'Login failed' 
      });
      throw error;
    }
  }

  // Logout method
  async logout(): Promise<void> {
    try {
      // Invalidate database session
      if (this.currentState.sessionToken) {
        await supabase.rpc('invalidate_other_sessions', {
          current_session_token: this.currentState.sessionToken
        });
      }

      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear local session
      this.clearSession();

      // Broadcast logout to other tabs
      this.broadcastEvent({ type: 'LOGOUT', payload: {} });

      debugSession('Logout completed');
    } catch (error) {
      debugSession('Error during logout', error);
      // Still clear local session even if remote logout fails
      this.clearSession();
    }
  }

  // Cleanup method
  cleanup(): void {
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
    }
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
    }
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
    this.listeners.clear();
  }
}

// Create singleton instance
export const centralizedSessionService = new CentralizedSessionService();
